import { useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Search, X, ArrowRight, Phone,
  Smartphone, Monitor, Wifi, Camera, Printer, KeyRound, GraduationCap,
} from "lucide-react";
import { searchHelp, CONTACT } from "../data";
import { VoiceButton } from "./VoiceButton";

const ICONS = { Smartphone, Monitor, Wifi, Camera, Printer, KeyRound, GraduationCap };

// Reusable "Find Help by voice" search.
// variant: "hero" (compact, on a dark hero) | "modal" (full, inside a dialog)
export const HelpSearch = ({ variant = "hero", onSelect }) => {
  const [query, setQuery] = useState("");
  const handleVoice = useCallback((text) => setQuery(text), []);

  const results = useMemo(() => searchHelp(query), [query]);
  const showResults = query.trim().length > 0;
  const isModal = variant === "modal";

  const EXAMPLES = ["Wi‑Fi is slow", "New phone setup", "Ring doorbell", "Printer won't print"];

  const bookHref = (title) => `/contact?service=${encodeURIComponent(title)}`;

  return (
    <div data-testid={`help-search-${variant}`} className="w-full">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-6 w-6 text-muted-foreground" />
          <input
            data-testid="help-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Tell us what you need — e.g. “my printer won’t work”"
            className="h-14 w-full rounded-full border-2 border-border bg-card pl-14 pr-12 text-lg text-foreground outline-none focus:border-accent transition-colors"
            aria-label="Search for help"
          />
          {query && (
            <button
              type="button"
              data-testid="help-search-clear"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute right-4 top-1/2 -translate-y-1/2 grid place-items-center h-9 w-9 rounded-full text-muted-foreground hover:bg-muted"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        <VoiceButton onResult={handleVoice} idleLabel="Tap to speak" testId="help-search-voice" />
      </div>

      {!showResults && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="help-search-examples">
          <span className="text-muted-foreground text-sm mr-1">Try:</span>
          {EXAMPLES.map((ex) => (
            <button
              key={ex}
              type="button"
              data-testid={`help-example-${ex.replace(/[^a-zA-Z]/g, "-").toLowerCase()}`}
              onClick={() => setQuery(ex)}
              className="h-9 px-4 rounded-full border border-border bg-card text-foreground text-sm font-medium hover:border-accent hover:text-accent transition-colors"
            >
              {ex}
            </button>
          ))}
        </div>
      )}

      {showResults && (
        <div
          data-testid="help-search-results"
          className={`mt-3 rounded-2xl border border-border bg-card p-3 shadow-xl ${
            isModal ? "" : "max-h-[360px] overflow-auto"
          }`}
        >
          {results.length === 0 ? (
            <div data-testid="help-search-empty" className="p-5 text-center">
              <p className="text-foreground font-semibold text-lg">
                We can still help with that!
              </p>
              <p className="mt-1 text-muted-foreground">
                Tell us more and we’ll point you the right way — or just give us a call.
              </p>
              <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={CONTACT.phoneHref}
                  data-testid="help-search-call"
                  className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-accent text-white font-bold hover:bg-[hsl(196,82%,27%)] transition-colors"
                >
                  <Phone className="h-5 w-5" /> Call {CONTACT.phone}
                </a>
                <Link
                  to="/contact"
                  onClick={onSelect}
                  data-testid="help-search-contact"
                  className="inline-flex items-center justify-center h-12 px-6 rounded-full border border-border text-foreground font-bold hover:border-accent transition-colors"
                >
                  Send us a message
                </Link>
              </div>
            </div>
          ) : (
            <ul className="space-y-2">
              {results.map((s) => {
                const Icon = ICONS[s.icon] || Search;
                return (
                  <li key={s.slug}>
                    <div
                      data-testid={`help-result-${s.slug}`}
                      className="flex items-start gap-4 rounded-xl p-3 hover:bg-muted transition-colors"
                    >
                      <span className="grid place-items-center h-11 w-11 rounded-xl bg-accent/10 text-accent shrink-0">
                        <Icon className="h-6 w-6" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-heading font-bold text-foreground text-lg leading-snug">
                          {s.title}
                        </p>
                        <p className="text-muted-foreground text-sm mt-0.5 line-clamp-2">
                          {s.short}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <Link
                            to={bookHref(s.title)}
                            onClick={onSelect}
                            data-testid={`help-book-${s.slug}`}
                            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-accent text-white font-bold text-sm hover:bg-[hsl(196,82%,27%)] transition-colors"
                          >
                            Book this <ArrowRight className="h-4 w-4" />
                          </Link>
                          <Link
                            to="/services"
                            onClick={onSelect}
                            data-testid={`help-learn-${s.slug}`}
                            className="inline-flex items-center h-10 px-4 rounded-full border border-border text-foreground font-semibold text-sm hover:border-accent transition-colors"
                          >
                            Learn more
                          </Link>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
